import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { invalidateSiteSettings } from '@/lib/siteSettings';
import { toast } from 'sonner';
import { ArrowLeft, Globe, BarChart3, Share2, Copy, ExternalLink, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MarketingDashboard = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [seoStatus, setSeoStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState('');
  const [form, setForm] = useState({
    meta_pixel_id: '',
    google_analytics_id: '',
    pinterest_tag_id: '',
    pinterest_url: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [settingsRes, seoRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/seo/ping-status`),
      ]);
      setSettings(settingsRes.data);
      setSeoStatus(seoRes.data);
      setForm({
        meta_pixel_id: settingsRes.data.meta_pixel_id || '',
        google_analytics_id: settingsRes.data.google_analytics_id || '',
        pinterest_tag_id: settingsRes.data.pinterest_tag_id || '',
        pinterest_url: settingsRes.data.pinterest_url || '',
      });
    } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      await axios.put(`${API}/settings`, fd, { headers: { Authorization: `Bearer ${token}` } });
      invalidateSiteSettings();
      toast.success('Marketing settings saved! Changes take effect on next page load.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(''), 2000);
  };

  const SITE_URL = 'https://gangadharsom.art';

  const guides = [
    {
      title: 'Google Search Console',
      subtitle: 'Get your art discovered on Google Search & Image Search',
      color: 'blue',
      steps: [
        { text: 'Go to Google Search Console', link: 'https://search.google.com/search-console' },
        { text: 'Add property → Enter: gangadharsom.art' },
        { text: 'Verify via HTML tag (already in your site) or DNS' },
        { text: 'Go to Indexing → Sitemaps → Submit:', copyable: `${SITE_URL}/api/sitemap.xml`, label: 'Sitemap URL' },
        { text: 'Google will start indexing your artworks within 1-3 days' },
      ],
      priority: 'HIGH — Free organic traffic from Google',
    },
    {
      title: 'Pinterest Business',
      subtitle: 'Art performs exceptionally well on Pinterest — drive free traffic',
      color: 'red',
      steps: [
        { text: 'Create a Pinterest Business account', link: 'https://business.pinterest.com' },
        { text: 'Claim your website: gangadharsom.art' },
        { text: 'Rich Pins are already enabled on your site (product meta tags)' },
        { text: 'Create a board "Gangadhar Som Paintings"' },
        { text: 'Pin your artwork pages — each has Pinterest-optimized OG tags' },
        { text: 'Optional: Get your Pinterest Tag ID and enter it below for conversion tracking' },
      ],
      priority: 'HIGH — Art is the #1 category on Pinterest',
    },
    {
      title: 'Meta (Facebook & Instagram)',
      subtitle: 'Track visitors and run targeted ads to art collectors',
      color: 'indigo',
      steps: [
        { text: 'Go to Meta Business Suite', link: 'https://business.facebook.com' },
        { text: 'Create a Pixel: Events Manager → Connect Data → Web → Meta Pixel' },
        { text: 'Copy your Pixel ID and enter it below' },
        { text: 'Your site automatically tracks: PageView, ViewContent, AddToCart, Purchase' },
        { text: 'Create ads targeting art collectors with your artwork images' },
      ],
      priority: 'MEDIUM — Paid ads + retargeting',
    },
    {
      title: 'Google Analytics 4',
      subtitle: 'Understand your visitors — where they come from, what they view',
      color: 'green',
      steps: [
        { text: 'Go to Google Analytics', link: 'https://analytics.google.com' },
        { text: 'Create a property for gangadharsom.art' },
        { text: 'Get your Measurement ID (starts with G-) from Admin → Data Streams' },
        { text: 'Enter the Measurement ID below' },
        { text: 'Tracks: page views, artwork views, add to cart, purchases' },
      ],
      priority: 'MEDIUM — Understand your audience',
    },
    {
      title: 'Instagram & Facebook Pages',
      subtitle: 'Post artworks with direct links to your gallery',
      color: 'pink',
      steps: [
        { text: 'Post artwork images with captions linking to your gallery' },
        { text: 'Share link format:', copyable: `${SITE_URL}/gallery?artwork=YOUR_ARTWORK_ID`, label: 'Artwork link' },
        { text: 'Use hashtags: #IndianArt #KrishnaArt #OilPainting #ArtForSale' },
        { text: 'Add your website link to Instagram bio' },
        { text: 'Use Instagram Stories with link stickers to artwork pages' },
      ],
      priority: 'HIGH — Free social media traffic',
    },
    {
      title: 'Google My Business',
      subtitle: 'Appear in local searches for "artist near me"',
      color: 'yellow',
      steps: [
        { text: 'Go to Google Business Profile', link: 'https://business.google.com' },
        { text: 'Create a profile as "Artist" or "Art Gallery"' },
        { text: 'Add your address (Bangalore/Mysore), phone, website' },
        { text: 'Upload your best artwork photos' },
        { text: 'Respond to reviews — builds trust and improves ranking' },
      ],
      priority: 'MEDIUM — Local discovery',
    },
  ];

  const colorMap = {
    blue: 'border-blue-200 bg-blue-50/50',
    red: 'border-red-200 bg-red-50/50',
    indigo: 'border-indigo-200 bg-indigo-50/50',
    green: 'border-green-200 bg-green-50/50',
    pink: 'border-pink-200 bg-pink-50/50',
    yellow: 'border-yellow-200 bg-yellow-50/50',
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="marketing-dashboard">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft size={16} /> Admin Dashboard
        </Link>
        <h1 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-2" data-testid="marketing-title">Marketing & Growth</h1>
        <p className="font-sans text-sm text-muted-foreground mb-8">Set up tracking pixels, submit to search engines, and grow your audience.</p>

        {/* Sitemap Stats */}
        {seoStatus?.sitemap_stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="p-4 bg-card border border-border rounded-lg text-center">
              <p className="font-serif text-2xl font-light">{seoStatus.sitemap_stats.artworks}</p>
              <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Artworks Indexed</p>
            </div>
            <div className="p-4 bg-card border border-border rounded-lg text-center">
              <p className="font-serif text-2xl font-light">{seoStatus.sitemap_stats.blog_posts}</p>
              <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Blog Posts</p>
            </div>
            <div className="p-4 bg-card border border-border rounded-lg text-center">
              <p className="font-serif text-2xl font-light">{seoStatus.sitemap_stats.collections}</p>
              <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Collections</p>
            </div>
            <div className="p-4 bg-card border border-border rounded-lg text-center">
              <p className="font-serif text-2xl font-light">4</p>
              <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Portrait Pages</p>
            </div>
          </div>
        )}

        {/* Tracking IDs Configuration */}
        <div className="bg-card border border-border rounded-lg p-6 mb-8" data-testid="tracking-config">
          <h2 className="font-sans text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 size={20} /> Tracking & Analytics Setup
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="meta_pixel_id">Meta (Facebook) Pixel ID</Label>
              <Input id="meta_pixel_id" value={form.meta_pixel_id} onChange={e => setForm({...form, meta_pixel_id: e.target.value})}
                placeholder="e.g. 1234567890123456" data-testid="meta-pixel-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Get from Meta Events Manager → Pixel</p>
            </div>
            <div>
              <Label htmlFor="google_analytics_id">Google Analytics Measurement ID</Label>
              <Input id="google_analytics_id" value={form.google_analytics_id} onChange={e => setForm({...form, google_analytics_id: e.target.value})}
                placeholder="e.g. G-XXXXXXXXXX" data-testid="ga-id-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Get from GA4 → Admin → Data Streams</p>
            </div>
            <div>
              <Label htmlFor="pinterest_tag_id">Pinterest Tag ID</Label>
              <Input id="pinterest_tag_id" value={form.pinterest_tag_id} onChange={e => setForm({...form, pinterest_tag_id: e.target.value})}
                placeholder="e.g. 1234567890" data-testid="pinterest-tag-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Get from Pinterest Ads → Conversions → Tag</p>
            </div>
            <div>
              <Label htmlFor="pinterest_url">Pinterest Profile URL</Label>
              <Input id="pinterest_url" value={form.pinterest_url} onChange={e => setForm({...form, pinterest_url: e.target.value})}
                placeholder="e.g. https://pinterest.com/gangadharsom" data-testid="pinterest-url-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Your Pinterest business profile URL</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-marketing-btn" className="bg-accent hover:bg-accent/90">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
            Save Tracking Settings
          </Button>
        </div>

        {/* Quick Copy Links */}
        <div className="bg-card border border-border rounded-lg p-6 mb-8" data-testid="quick-links">
          <h2 className="font-sans text-lg font-semibold mb-4 flex items-center gap-2">
            <Share2 size={20} /> Quick Copy Links
          </h2>
          <div className="space-y-3">
            {[
              { label: 'Sitemap URL', value: `${SITE_URL}/api/sitemap.xml` },
              { label: 'Gallery URL', value: `${SITE_URL}/gallery` },
              { label: 'Portraits URL', value: `${SITE_URL}/portraits` },
              { label: 'Commissions URL', value: `${SITE_URL}/commissions` },
              { label: 'Contact URL', value: `${SITE_URL}/contact` },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between bg-background border border-border/50 rounded-lg px-4 py-2.5">
                <div>
                  <span className="font-sans text-xs font-medium">{item.label}</span>
                  <span className="text-xs text-muted-foreground ml-2">{item.value}</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(item.value, item.label)}
                  className="h-7 text-xs">
                  {copied === item.label ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Step-by-step Guides */}
        <h2 className="font-sans text-lg font-semibold mb-4 flex items-center gap-2">
          <Globe size={20} /> Growth Playbook — Step-by-Step Guides
        </h2>
        <div className="space-y-4">
          {guides.map((guide, i) => (
            <div key={i} className={`border rounded-lg p-5 ${colorMap[guide.color]}`} data-testid={`guide-${i}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-sans font-semibold text-base">{guide.title}</h3>
                  <p className="font-sans text-xs text-muted-foreground">{guide.subtitle}</p>
                </div>
                <span className={`text-[9px] font-sans uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                  guide.priority.startsWith('HIGH') ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>{guide.priority.split(' — ')[0]}</span>
              </div>
              <ol className="space-y-2">
                {guide.steps.map((step, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center text-[10px] font-medium mt-0.5">{j + 1}</span>
                    <div className="flex-1">
                      <span>{step.text}</span>
                      {step.link && (
                        <a href={step.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 ml-1 text-blue-600 hover:underline text-xs">
                          Open <ExternalLink size={10} />
                        </a>
                      )}
                      {step.copyable && (
                        <button onClick={() => copyToClipboard(step.copyable, step.label)}
                          className="inline-flex items-center gap-1 ml-1 text-xs text-blue-600 hover:underline">
                          <code className="bg-background px-1.5 py-0.5 rounded text-[10px] border border-border">{step.copyable}</code>
                          {copied === step.label ? <Check size={10} className="text-green-500" /> : <Copy size={10} />}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketingDashboard;
